const STALE_ASSET_RELOAD_KEY = "vs_stale_asset_reload";
const STALE_ROUTE_RELOAD_KEY = "vs_stale_route_reload";
const PWA_RETIRE_KEY = "vs_pwa_runtime_retired";
const PWA_RETIRE_VERSION = "disable-sw-20260708";
const PWA_RETIRE_PARAM = "_pwa_reset";
let pageIsUnloading = false;

const markPageUnloading = () => {
  pageIsUnloading = true;
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", markPageUnloading);
  window.addEventListener("pagehide", markPageUnloading);
}

export const isStaleAssetError = (input: unknown) => {
  const message =
    typeof input === "string"
      ? input
      : input instanceof Error
        ? input.message
        : (input as { message?: string } | null)?.message || "";
  const m = message.toLowerCase();

  return (
    m.includes("importing a module script failed") ||
    m.includes("failed to fetch dynamically imported module") ||
    m.includes("error loading dynamically imported module") ||
    m.includes("unable to preload css") ||
    m.includes("unable to preload") ||
    m.includes("loading chunk") ||
    m.includes("chunkloaderror") ||
    m.includes("failed to load module script") ||
    (m.includes("unexpected token") && m.includes("<"))
  );
};

export const clearPwaRuntimeState = async () => {
  try {
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    }
  } catch {
    // Best-effort cleanup only.
  }

  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    }
  } catch {
    // Best-effort cleanup only.
  }
};

const isVoltyShareProductionHost = () =>
  typeof window !== "undefined" &&
  (window.location.hostname === "voltyshare.com" || window.location.hostname === "www.voltyshare.com");

export const retirePwaRuntimeForReleaseStability = async () => {
  if (!isVoltyShareProductionHost()) return false;
  if (!("serviceWorker" in navigator)) return false;

  const controlledByServiceWorker = !!navigator.serviceWorker.controller;
  let alreadyRetired = false;
  try {
    alreadyRetired = localStorage.getItem(PWA_RETIRE_KEY) === PWA_RETIRE_VERSION;
  } catch {
    alreadyRetired = false;
  }

  if (alreadyRetired && !controlledByServiceWorker) return false;

  await clearPwaRuntimeState();

  try {
    localStorage.setItem(PWA_RETIRE_KEY, PWA_RETIRE_VERSION);
  } catch {
    // Best-effort marker only.
  }

  if (!controlledByServiceWorker) return false;

  const url = new URL(location.href);
  if (url.searchParams.has(PWA_RETIRE_PARAM)) return false;
  url.searchParams.set(PWA_RETIRE_PARAM, Date.now().toString(36));
  location.replace(url.toString());
  return true;
};

export const recoverFromStaleAssets = (error?: unknown) => {
  // A full-page navigation aborts pending lazy imports. Treating that expected
  // cancellation as a stale deploy can redirect the user back to the old page.
  if (pageIsUnloading) return false;
  if (error && !isStaleAssetError(error)) return false;

  const last = Number(sessionStorage.getItem(STALE_ASSET_RELOAD_KEY) || "0");
  if (Date.now() - last < 30_000) return false;

  sessionStorage.setItem(STALE_ASSET_RELOAD_KEY, String(Date.now()));

  void (async () => {
    if (pageIsUnloading) return;
    await clearPwaRuntimeState();
    if (pageIsUnloading) return;
    const url = new URL(location.href);
    url.searchParams.set("_v", Date.now().toString(36));
    location.replace(url.toString());
  })();

  return true;
};

export const recoverFromStaleRoute = (path = location.pathname) => {
  if (!path.startsWith("/admin/")) return false;

  const last = Number(sessionStorage.getItem(STALE_ROUTE_RELOAD_KEY) || "0");
  if (Date.now() - last < 30_000) return false;

  sessionStorage.setItem(STALE_ROUTE_RELOAD_KEY, String(Date.now()));

  void (async () => {
    await clearPwaRuntimeState();
    const url = new URL(location.href);
    url.searchParams.set("_v", Date.now().toString(36));
    location.replace(url.toString());
  })();

  return true;
};
